// Two-segment stacked bars (K gold / P green) on ruled paper. Pure SVG.
import React from 'react';

export interface BarSegment { value: number; color: string; label: string }
export interface Bar { x: number; segments: BarSegment[] }

interface Props {
  bars: Bar[];
  height?: number;
  ariaLabel: string;
}

const W = 600;
const PAD = { top: 12, right: 16, bottom: 22, left: 34 };

const StackedBars: React.FC<Props> = ({ bars, height = 180, ariaLabel }) => {
  if (bars.length === 0) return null;
  const H = height;
  const totals = bars.map(b => b.segments.reduce((a, s) => a + s.value, 0));
  const yMax = Math.ceil(Math.max(1, ...totals) * 1.1);
  const innerW = W - PAD.left - PAD.right;
  const slot = innerW / bars.length;
  const barW = Math.min(28, slot * 0.55);
  const py = (y: number) => H - PAD.bottom - (y / yMax) * (H - PAD.top - PAD.bottom);
  const gridYs = [0.25, 0.5, 0.75, 1].map(f => yMax * f);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={ariaLabel}>
      {gridYs.map(g => (
        <g key={g}>
          <line x1={PAD.left} x2={W - PAD.right} y1={py(g)} y2={py(g)} stroke="#CFCDC6" strokeWidth="0.5" />
          <text x={PAD.left - 6} y={py(g) + 3} fontSize="10" fill="#6E6960" textAnchor="end">{Math.round(g)}</text>
        </g>
      ))}
      {bars.map((b, i) => {
        const cx = PAD.left + slot * i + slot / 2;
        let yCursor = 0;
        return (
          <g key={b.x}>
            {b.segments.map(s => {
              const y0 = yCursor; yCursor += s.value;
              return (
                <rect
                  key={s.label}
                  x={cx - barW / 2}
                  y={py(yCursor)}
                  width={barW}
                  height={Math.max(0, py(y0) - py(yCursor))}
                  fill={s.color}
                />
              );
            })}
            <title>{`W${b.x}: ${b.segments.map(s => `${s.label} ${s.value}`).join(' · ')}`}</title>
            <text x={cx} y={H - 6} fontSize="10" fill="#6E6960" textAnchor="middle">W{b.x}</text>
          </g>
        );
      })}
    </svg>
  );
};

export default StackedBars;
